import { EventRuntimeEngineServiceInfoClientChangeFeed } from "@hds-products/event-runtime-entities-client";
import { useEventRuntimeEngineServiceInfoStore } from '../pinia/eventRuntimeEngineServiceInfoStore';
import { useAdminSdkStatusStore } from '../pinia/adminSdkStatusStore';
import { compare } from 'fast-json-patch';

class EventRuntimeEngineServiceInfoPiniaVueAdapter {
    #_changeFeed;
    #_deltaServerClient;
    #_ereServiceInfoPiniaStore;
    #_sdkStatusPiniaStore;
    #_ereServiceInfoWatcher;
    constructor(deltaServerClient, ereServiceInfoBaseUri, customHeaderFn) {
        this.#_deltaServerClient = deltaServerClient;
        this.#_changeFeed = new EventRuntimeEngineServiceInfoClientChangeFeed(deltaServerClient, ereServiceInfoBaseUri, customHeaderFn, null);
        this.#_ereServiceInfoPiniaStore = useEventRuntimeEngineServiceInfoStore();
        this.#_sdkStatusPiniaStore = useAdminSdkStatusStore();
        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('ere-service-info-state', { title: 'Service Info', description: 'Runtime engine service discovery', status: 'INITIALIZING' })
        this.onAdded = this.onAdded.bind(this);
        this.onUpdated = this.onUpdated.bind(this);
        this.onRemoved = this.onRemoved.bind(this);
    }
    async _connectAndStartChangeFeed(isRestart = false) {
        let attempts = 1;
        if (this.#_deltaServerClient.isConnected()) {
            await this.#_changeFeed.whenStarted();
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('ere-service-info-state', { status: 'CONNECTED', attempts })
        } else {
            while (!this.#_deltaServerClient.isConnected()) {
                try {
                    await this.#_deltaServerClient.whenConnected();
                    if (attempts === 1) {
                        console.info(`ere-service-info-state changefeed connected`);
                    } else {
                        console.info(`ere-service-info-state changefeed connected after attempt #${attempts}.`);
                    }
                    if (isRestart) {
                        await this.#_changeFeed.restart();
                    } else {
                        await this.#_changeFeed.whenStarted();
                    }
                    attempts = 1;
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('ere-service-info-state', { status: 'CONNECTED', attempts })
                } catch (err) {
                    //A refused principal stays refused, so another attempt cannot succeed
                    if (err?.closeCode === 4403) {
                        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('ere-service-info-state', { status: 'DISCONNECTED', attempts })
                        return;
                    }
                    attempts++;
                    console.error(`Attempt #${attempts}: ere-service-info-state changefeed error: ${err}`);
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('ere-service-info-state', { status: 'DISCONNECTED', attempts })
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
        }
    }
    async initialize() {
        this.#_changeFeed.addFailureListener(async (error) => {
            console.error(`Change Feed Failure (EREServiceInfoChangeFeed) => ${error.message}`);
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('ere-service-info-state', { status: 'DISCONNECTED' })
            await this._connectAndStartChangeFeed(true);
        }, this);
        await this._connectAndStartChangeFeed();
    }
  
    async watchAllEreServiceInfo(ids = []) {
        if (this.#_ereServiceInfoWatcher) {
            return;
        } else {
            this.#_ereServiceInfoWatcher = await this.#_changeFeed.watchAll();
            this.#_ereServiceInfoWatcher.on('entity-added', this.onAdded);
            this.#_ereServiceInfoWatcher.on('entity-updated', this.onUpdated);
            this.#_ereServiceInfoWatcher.on('entity-removed', this.onRemoved);
            this.#_ereServiceInfoWatcher.entities?.forEach((value, key) => {
                this.onAdded(value);
            })
        }
    }
    async unwatchAllEreServiceInfo() {
        if (!this.#_ereServiceInfoWatcher) { 
            return; 
        } else {
            this.#_ereServiceInfoWatcher.off('entity-added', this.onAdded);
            this.#_ereServiceInfoWatcher.off('entity-updated', this.onUpdated);
            this.#_ereServiceInfoWatcher.off('entity-removed', this.onRemoved);
            this.#_ereServiceInfoWatcher.entities?.forEach((value, key) => {
                this.onRemoved(value.id);
            })
            this.#_changeFeed.unwatch(this.#_ereServiceInfoWatcher.watchId);
            this.#_ereServiceInfoWatcher = null;
        }
    }
    onAdded(ereServiceInfoEntity) {
        this.#_ereServiceInfoPiniaStore.addEventRuntimeEngineServiceInfo(ereServiceInfoEntity.toJSON());
    }
    onUpdated(ereServiceInfoId, preModel, newEntity) {
        const jsonPatch = compare(preModel, newEntity.toJSON());
        if (jsonPatch.length > 0) {
            this.#_ereServiceInfoPiniaStore.applyJsonPatch(ereServiceInfoId, jsonPatch);
        }
    }
    onRemoved(ereServiceInfoId) {
        this.#_ereServiceInfoPiniaStore.removeEventRuntimeEngineServiceInfo(ereServiceInfoId);
    }
}

export { EventRuntimeEngineServiceInfoPiniaVueAdapter }
export default EventRuntimeEngineServiceInfoPiniaVueAdapter;